import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["device"]
  static values = { autoSelect: { type: Boolean, default: false } }

  connect() {
    const visible = this.visibleDevices()
    if (this.autoSelectValue && visible.length > 0) {
      const selected = visible.find(el =>
        el.classList.contains("radar-interface__device-item--selected")
      ) || visible[0]
      this.pendingDevice = selected
      this.scheduleClick()
    }
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeyDown)

    this.boundOnDevicesFrameLoad = () => {
      requestAnimationFrame(() => this.updateTabIndexes())
    }
    this.element.closest("turbo-frame")?.addEventListener("turbo:frame-load", this.boundOnDevicesFrameLoad)

    this.boundSyncDeviceId = (event) => {
      const newFrame = event.detail.newFrame
      if (newFrame) {
        const deviceId = newFrame.getAttribute("data-current-device-id")
        if (deviceId !== null) {
          event.target.setAttribute("data-current-device-id", deviceId)
        } else {
          event.target.removeAttribute("data-current-device-id")
        }
      }
    }
    document.getElementById("device_panel")
      ?.addEventListener("turbo:before-frame-render", this.boundSyncDeviceId)

    this.updateTabIndexes()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeyDown)
    this.boundHandleKeyDown = null
    this.pendingDevice = null
    this.navigating = false
    this.cleanupFrameListeners?.()
    this.element.closest("turbo-frame")?.removeEventListener("turbo:frame-load", this.boundOnDevicesFrameLoad)
    document.getElementById("device_panel")
      ?.removeEventListener("turbo:before-frame-render", this.boundSyncDeviceId)
  }

  select(event) {
    this.deviceTargets.forEach(el => el.classList.remove("radar-interface__device-item--selected"))
    event.currentTarget.classList.add("radar-interface__device-item--selected")
    this.updateTabIndexes()
  }

  handleKeyDown(event) {
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (this.isUserTyping()) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this.handleArrowKey(event)
    } else if (event.key === "Tab") {
      this.handleTab(event)
    }
  }

  handleArrowKey(event) {
    const visible = this.visibleDevices()
    if (visible.length === 0) return

    event.preventDefault()
    const currentIndex = visible.findIndex(el =>
      el.classList.contains("radar-interface__device-item--selected")
    )

    let nextIndex
    if (event.key === "ArrowDown") {
      nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % visible.length
    } else {
      nextIndex = currentIndex === -1
        ? visible.length - 1
        : (currentIndex - 1 + visible.length) % visible.length
    }

    const next = visible[nextIndex]

    this.deviceTargets.forEach(el => el.classList.remove("radar-interface__device-item--selected"))
    next.classList.add("radar-interface__device-item--selected")
    next.scrollIntoView({ block: "nearest" })
    next.focus({ preventScroll: true })

    this.updateTabIndexes()
    this.pendingDevice = next
    this.scheduleClick()
  }

  handleTab(event) {
    const focusables = this.getDetailFocusables()
    const active = focusables.includes(event.target)
      ? event.target
      : document.activeElement

    if (event.shiftKey) {
      const headersBelow = this.getNetworkHeadersBelowSelected()
      const headerBelowIdx = headersBelow.indexOf(active)
      if (headerBelowIdx >= 0) {
        event.preventDefault()
        if (headerBelowIdx === 0) {
          const chain = this.getDetailFocusables()
          const tail = chain[chain.length - 1]
          tail?.focus()
        } else {
          headersBelow[headerBelowIdx - 1].focus()
        }
        return
      }

      const idx = focusables.indexOf(active)
      if (idx === 0) {
        event.preventDefault()
        this.selectedDevice?.focus()
      } else if (idx > 0) {
        event.preventDefault()
        focusables[idx - 1].focus()
      }
      return
    }

    const headersBelow = this.getNetworkHeadersBelowSelected()
    const headerBelowIdx = headersBelow.indexOf(active)
    if (headerBelowIdx >= 0) {
      if (headerBelowIdx === headersBelow.length - 1) {
        event.preventDefault()
        this.focusFirstOnPage()
      }
      return
    }

    const idx = focusables.indexOf(active)
    const selectedEl = this.selectedDevice
    if (selectedEl && active === selectedEl) {
      const first = focusables[0]
      if (first) {
        event.preventDefault()
        first.focus()
      }
      return
    }

    if (idx >= 0 && idx < focusables.length - 1) {
      event.preventDefault()
      focusables[idx + 1].focus()
      return
    }

    if (focusables.length > 0 && idx === focusables.length - 1) {
      const nextHeader = this.getFirstNetworkHeaderBelow()
      if (nextHeader) {
        event.preventDefault()
        nextHeader.focus()
      } else {
        event.preventDefault()
        this.focusFirstOnPage()
      }
    }
  }

  focusFirstOnPage() {
    const first = document.querySelector(
      'a[href], button:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea, [tabindex="0"]',
    )
    first?.focus()
  }

  getDetailFocusables() {
    const panel = document.getElementById("device_panel")
    if (!panel) return []

    const fields = [...panel.querySelectorAll(".radar-interface__field-display")]

    const header = panel.querySelector("#device_details_header")
    const headerFocusables = []
    if (header) {
      const assetLink = header.querySelector(".radar-interface__details__device-info__ip--link")
      if (assetLink) headerFocusables.push(assetLink)

      const refreshBtn = header.querySelector(
        "#device_refresh_button button, #device_refresh_button input[type='submit']",
      )
      if (refreshBtn) headerFocusables.push(refreshBtn)

      const deleteBtn = header.querySelector("a.button--danger--unfilled")
      if (deleteBtn) headerFocusables.push(deleteBtn)
    }

    const fullScanToggle = panel.querySelector(
      ".radar-interface__details__full-scan__header .radar-interface__collapse-toggle",
    )
    const scanFocusables = fullScanToggle ? [fullScanToggle] : []

    return [...fields, ...headerFocusables, ...scanFocusables]
  }

  getFirstNetworkHeaderBelow() {
    const headers = this.getNetworkHeadersBelowSelected()
    return headers[0] ?? null
  }

  /** Visible network group headers in DOM order after the selected device's network group. */
  getNetworkHeadersBelowSelected() {
    const selected = this.selectedDevice
    if (!selected) return []

    const networkGroup = selected.closest(".radar-interface__network-group")
    if (!networkGroup) return []

    const headers = []
    let sibling = networkGroup.nextElementSibling
    while (sibling) {
      if (sibling.classList?.contains("radar-interface__network-group")) {
        const header = sibling.querySelector(".radar-interface__network-group__header")
        if (header && header.offsetParent !== null) headers.push(header)
      }
      sibling = sibling.nextElementSibling
    }
    return headers
  }

  get selectedDevice() {
    return this.deviceTargets.find(el =>
      el.classList.contains("radar-interface__device-item--selected"),
    ) ?? null
  }

  visibleDevices() {
    return this.deviceTargets.filter(el =>
      el.style.display !== "none" && el.offsetParent !== null
    )
  }

  updateTabIndexes() {
    this.deviceTargets.forEach(el => {
      const selected = el.classList.contains("radar-interface__device-item--selected")
      el.tabIndex = selected ? 0 : -1
    })
  }

  // Queue device clicks so we don't kick off a new turbo-frame navigation
  // while another is already in flight. Otherwise Turbo aborts the in-flight
  // request and floods the console with AbortError rejections.
  scheduleClick() {
    if (this.navigating || !this.pendingDevice) return

    const target = this.pendingDevice
    this.pendingDevice = null

    const panel = document.getElementById("device_panel")
    const pendingId = target.id.replace("device_list_item_", "")
    const currentId = panel?.dataset?.currentDeviceId
    if (pendingId && currentId && pendingId === currentId) {
      this.updateTabIndexes()
      return
    }

    this.navigating = true

    const frame = document.getElementById("device_panel")

    const onLoad = () => {
      cleanup()
      this.scheduleClick()
    }

    const cleanup = () => {
      this.navigating = false
      frame?.removeEventListener("turbo:frame-load", onLoad)
      frame?.removeEventListener("turbo:fetch-request-error", cleanup)
      frame?.removeEventListener("turbo:frame-missing", cleanup)
    }
    this.cleanupFrameListeners = cleanup

    frame?.addEventListener("turbo:frame-load", onLoad, { once: true })
    frame?.addEventListener("turbo:fetch-request-error", cleanup, { once: true })
    frame?.addEventListener("turbo:frame-missing", cleanup, { once: true })

    target.click()
  }

  isUserTyping() {
    const el = document.activeElement
    if (!el) return false
    if (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return true
    return el.isContentEditable
  }
}
